// Maps to backend/routers/users.py
import { useState, useCallback, useRef, useEffect } from 'react';
import { env } from '../utils/env';

interface User {
  id: string;
  display_name: string;
  email: string;
  username?: string;
  avatar_url?: string;
  status?: 'active' | 'pending' | 'ghost';
  role?: 'owner' | 'admin' | 'member';
  joined_at?: string;
}

interface SearchResponse {
  users: User[];
  total_count: number;
}

export const useUserSearch = (
  accessToken: string,
  debounceMs: number = 300,
  minQueryLength: number = 2
) => {
  const [results, setResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const handleApiError = async (response: Response, defaultMessage: string) => {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `${defaultMessage}: ${response.status}`);
  };

  const searchUsers = useCallback(async (query: string) => {
    if (!accessToken || query.length < minQueryLength) {
      // Do not clear existing results immediately; this preserves selected labels.
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    // Set a debounce timeout
    timeoutRef.current = window.setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${env.API_URL}/api/users/search?q=${encodeURIComponent(query.trim())}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          credentials: 'include',
        });

        if (!response.ok) {
          await handleApiError(response, 'Failed to search users');
        }

        const data: SearchResponse = await response.json();
        setResults(data.users);
      } catch (err) {
        console.error('Error searching users:', err);
        setError(err instanceof Error ? err.message : 'Failed to search users');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs);
  }, [accessToken, minQueryLength, debounceMs]);

  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    results,
    loading,
    error,
    searchUsers,
    clearResults,
  };
}; 